import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { array, arrayOf, bool, func, object, shape, string } from 'prop-types';
import find from 'lodash/find';
import map from 'lodash/map';
import trim from 'lodash/trim';

import styles from './styles';
import { getI18n, getI18nFormattedMessage } from '../../../../../utils/intl';
import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  withStyles,
} from '@material-ui/core';

import {
  FormDialog,
  FormTextField,
  TableMultiValueCell,
} from '../../../../../components';
import { fetchUsers } from '../../../../../actions/user';
import { getIsLoading, getUserListResults } from '../../../../../reducers/user';
import { deduceUserType } from '../../utils';
import {
  USER_TYPE_GLOBAL,
} from "../../../../../constants";

export const LookupByEmailDialog = (props) => {
  const {
    authConfigUuid,
    classes,
    fetchUsers,
    isExecutingUserSearchQuery,
    isOpen,
    onCancel,
    onSubmit,
    userContext,
    userSearchResults,
  } = props;
  const intl = getI18n(useIntl());

  const [selectedUserName, setSelectedUserName] = useState('');
  const [searchByEmailResults, setSearchByEmailResults] = useState([]);
  const [emailSearchText, setEmailSearchText] = useState('');
  const { portalAdmin } = userContext;
  const [searchTriggered, setSearchTriggered] = useState(false);

  const Organization = ({ access, expanded }) => (
    <TableMultiValueCell
      entity={getI18nFormattedMessage('label.organizations')}
      text={(role) => (role.orgName && `${role.orgName}`)}
      items={access}
      expanded={expanded}
      uniqueKey={(item) => `${item.orgUuid}`}
    />
  );

  Organization.propTypes = {
    access: arrayOf(shape({})),
    expanded: bool,
  };

  const handleClickOnRow = (uuid) => {
    const updatedRows = searchByEmailResults.map(item => {
      if (item.uuid === uuid) {
        if (item.expanded) {
          item.expanded = false;
        } else {
          item.expanded = true;
        }
      } else {
        item.expanded = false;
      }
      return item;
    });
    setSearchByEmailResults(updatedRows);
  };

  const renderUserName = (username) =>
    (<div data-apim-test={username} className={classes.userContainer}>
      <div className={classes.userName}>
        {username}
      </div>
    </div>);

  const renderUser = (user) => {
    let label = '';
    const { access, expanded, username, uuid } = user;
    if (portalAdmin) {
      const userType = deduceUserType(access);
      if (userType === USER_TYPE_GLOBAL) {
        label = renderUserName(username);
      } else {
        label = <div data-apim-test={username} className={classes.userContainer}>
          <div title={username} className={classes.userName}>
            {username}
          </div>
          <div
            className={classes.organizations}
            onClick={() => handleClickOnRow(uuid)}
          >
            <Organization access={access} expanded={expanded} />
          </div>
        </div>;
      }
    } else {
      label = renderUserName(username);
    }
    return (<FormControlLabel
      classes={{
        root: classes.userRow,
      }}
      control={<Radio />}
      label={label}
      value={username}
    />);
  };

  const handleSearch = () => {
    setSearchTriggered(true);
    fetchUsers({
      authConfigUuid,
      email: trim(emailSearchText),
      includeAnonymous: true,
      isExactMatch: true,
      organizationCheck: 'add',
    });
  };

  const handleEmailKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleUserSelection = (event) => {
    setSelectedUserName(event.target.value);
  };

  useEffect(() => {
    if (isOpen) {
      setEmailSearchText('');
      setSearchByEmailResults([]);
      setSelectedUserName('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSearchByEmailResults(userSearchResults);
    }
  }, [isExecutingUserSearchQuery]);

  const handleUserSearchDialogSubmit = () => {
    const selectedUser = find(searchByEmailResults, item => item.username === selectedUserName);
    onSubmit(selectedUser);
  };

  const renderTableHeader = () => {
    if (portalAdmin) {
      return (<div className={classes.userContainer}>
        <div className={classes.userName}>
          Username
        </div>
        <div className={classes.organizations}>
          Organization
        </div>
      </div>);
    }
    return (<div className={classes.userContainer}>
        <div className={classes.userName}>
          Username
        </div>
      </div>);
  };
  
  const showNoResultsMsg = searchTriggered &&
    (!searchByEmailResults || (searchByEmailResults.length === 0));

  return (
    <FormDialog
      isOpen={isOpen}
      cancelText={intl.getI18nMessage('label.cancel.button')}
      submitText={intl.getI18nMessage('label.user.details.idp.email.lookup.choose')}
      title={intl.getI18nMessage('label.user.details.idp.email.lookup.title')}
      dialogId={'seach-with-email-dialog'}
      handleClose={onCancel}
      onSubmit={handleUserSearchDialogSubmit}
      onCancel={onCancel}
      paperClass={classes.emailSearchDialogPaper}
      submitButtonDisabled={!selectedUserName}
    >
      <div className={classes.topRow}>
        <FormTextField
          fieldContainerClass={classes.emailContainer}
          handleChange={setEmailSearchText}
          helperText={''}
          hideLabel={false}
          name={''}
          onKeyPress={handleEmailKeyPress}
          placeholder={intl.getI18nMessage('label.user.details.idp.email.lookup.placeholder')}
          value={emailSearchText}
        />
        <Button
          className={classes.saveButton}
          color="secondary"
          data-apim-test="search-by-email-search"
          disabled={!emailSearchText}
          onClick={handleSearch}
          variant="contained"
        >
          {intl.getI18nMessage('label.search.button')}
        </Button>
      </div>
      <div className={classes.userHeaderRow}>
        {renderTableHeader()}
      </div>
      <div data-apim-test="no-results-message-div">
        {showNoResultsMsg && <span>{intl.getI18nMessage('label.user.details.idp.username.lookup.no.results')}</span>}
      </div>
      <RadioGroup
        aria-label="matching-users-by-email"
        name="matching-users-by-email"
        onChange={handleUserSelection}
        value={selectedUserName}
      >
        {map(searchByEmailResults, renderUser)}
      </RadioGroup>
    </FormDialog>
  );
};

const mapStateToProps = state => ({
  isExecutingUserSearchQuery: getIsLoading(state),
  userSearchResults: getUserListResults(state),
});

const mapDispatchToProps = {
  fetchUsers,
};

LookupByEmailDialog.propTypes = {
  authConfigUuid: string,
  classes: object,
  fetchUsers: func,
  onCancel: func,
  onSubmit: func,
  isExecutingUserSearchQuery: bool,
  isOpen: bool,
  userContext: object,
  userSearchResults: array,
}

LookupByEmailDialog.displayName = 'LookupByEmail';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(LookupByEmailDialog);
